/* Metric sub-pages — drill-downs from the four dashboard stat cards.
   Charts are pure CSS/SVG (no libs).

   LIVE wiring (item 7, 2026-06-01-late): hero numbers + verdict/source/client
   breakdowns + avg-elapsed-per-stage all read from MAV.metrics + MAV.summary
   + MAV.costByStage when present, falling back to the prototype's mock arrays
   when the API hasn't hydrated. Monthly trends (weekly[] arrays), KPI tiles
   like "Busiest day"/"Avg review time"/"Projected spend", and prose notes
   stay mock — they require time-series telemetry the swarm doesn't emit yet. */

/* ---------- shared live-data helpers ---------- */

function metricsClientName(slug) {
  if (!slug || slug === '__none__') return 'Unassigned';
  const c = window.MAV.clients && window.MAV.clients.find((x) => x.slug === slug);
  return (c && c.name) || slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

// Returns live verdict/source/client/avg-stage rows when MAV.metrics is
// populated; otherwise null (caller falls back to the existing mock arrays).
function liveMetricsView() {
  const M = window.MAV;
  if (!M || !M.metrics) return null;
  const m = M.metrics;
  const verdictTotal = (m.verdictBreakdown || []).reduce((s, v) => s + Number(v.count), 0);
  const pctOf = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;
  const verdictCount = (label) => {
    const row = (m.verdictBreakdown || []).find((v) => v.verdict === label);
    return row ? Number(row.count) : 0;
  };
  const sourceTotal = (m.sourceBreakdown || []).reduce((s, r) => s + Number(r.count), 0);
  const clientRows = [...(m.clientBreakdown || [])]
    .map((c) => ({
      slug: c.client_slug,
      name: metricsClientName(c.client_slug),
      builds: Number(c.builds || 0),
      cost: Number(c.cost || 0),
      passed: Number(c.passed || 0),
    }));
  const clientBuildsMax = clientRows.reduce((a, r) => Math.max(a, r.builds), 1);
  const clientCostMax = clientRows.reduce((a, r) => Math.max(a, r.cost), 0.0001);
  return {
    isLive: true,
    verdict: {
      pass: verdictCount('PASS'),
      conditional: verdictCount('CONDITIONAL'),
      fail: verdictCount('FAIL'),
      nullCount: verdictCount(null),
      total: verdictTotal,
      pctOf: (label) => pctOf(verdictCount(label), verdictTotal),
    },
    source: {
      total: sourceTotal,
      rows: (m.sourceBreakdown || []).map((r) => ({
        label: String(r.source || 'unknown').toUpperCase(),
        count: Number(r.count),
        pct: pctOf(Number(r.count), sourceTotal),
      })),
    },
    clients: {
      buildsRows: clientRows
        .slice(0, 6)
        .map((r) => ({ label: r.name, value: String(r.builds), pct: Math.round((r.builds / clientBuildsMax) * 100) })),
      costRows: clientRows
        .slice(0)
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 6)
        .map((r) => ({ label: r.name, value: '$' + r.cost.toFixed(2), pct: Math.round((r.cost / clientCostMax) * 100) })),
      passRows: clientRows
        .filter((r) => r.builds >= 1)
        .sort((a, b) => (b.passed / b.builds) - (a.passed / a.builds))
        .slice(0, 6)
        .map((r) => {
          const p = Math.round((r.passed / r.builds) * 100);
          return { label: r.name, value: `${p}%`, pct: p, color: p >= 90 ? 'var(--pass)' : 'var(--cond)' };
        }),
    },
    avgStageRows: (m.avgElapsedByStage || [])
      .sort((a, b) => parseFloat(a.no) - parseFloat(b.no))
      .map((s) => ({ label: s.name, seconds: Math.round(Number(s.avg_s) || 0) })),
    // Cost-by-model rows for the MetricSpend card. Model strings like
    // "google/gemini-3.5-flash-20260519" get shortened to "gemini-3.5-flash"
    // (strip provider prefix + trailing date) to match the prototype's chip
    // style; full provider name kept in a `fullModel` field for tooltips.
    costByModelRows: (() => {
      const rows = m.costByModel || [];
      const max = rows.reduce((a, r) => Math.max(a, Number(r.cost)), 0.0001);
      const short = (model) => {
        if (!model) return '—';
        const tail = String(model).split('/').pop();
        return tail.replace(/-\d{8}$/, '');
      };
      return rows.map((r) => ({
        label: short(r.model),
        fullModel: r.model,
        cost: Number(r.cost),
        tokens: Number(r.tokens || 0),
        runs: Number(r.runs || 0),
        pct: Math.round((Number(r.cost) / max) * 100),
        value: '$' + Number(r.cost).toFixed(2),
        color: 'var(--grad-warm)',
      }));
    })(),
  };
}

/* Metric sub-pages — drill-downs from the four dashboard stat cards. */

/* ---------- shared chart helpers ---------- */

function Trend({ data, color = 'var(--brand-orange)', height = 80 }) {
  const max = Math.max(...data), min = Math.min(...data), span = (max - min) || 1;
  const w = 100, pad = 6;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    height - pad - ((v - min) / span) * (height - pad * 2),
  ]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L ${w} ${height} L 0 ${height} Z`;
  return (
    <svg className="trend" viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ height }}>
      <path d={area} fill={color} opacity="0.12" />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"
        strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Bars({ data, color = 'var(--grad-warm)', height = 130, xlabels, fill }) {
  const max = Math.max(...data) || 1;
  // `fill` makes the chart grow to fill its (flex-column) card so it sits flush
  // with a taller sibling card instead of leaving a dead gap. Few-bar datasets
  // (e.g. a workspace with only a couple of build-days) get capped bar widths
  // via .bars-box--fill so they read as a tidy cluster, not giant blocks.
  return (
    <div className={'bars-box' + (fill ? ' bars-box--fill' : '')}>
      <div className="bars" style={fill ? undefined : { height }}>
        {data.map((v, i) => (
          <div key={i} className="bar-col">
            <div className="bar" style={{ height: (v / max * 100) + '%', background: color }} title={String(v)}></div>
          </div>
        ))}
      </div>
      {xlabels && <div className="bars-x">{xlabels.map((l, i) => <span key={i}>{l}</span>)}</div>}
    </div>
  );
}

function Breakdown({ rows }) {
  // rows: [{ label, pct, value, color }]
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} className="bd-row">
          <div className="bd-top"><span className="bd-l">{r.label}</span><span className="bd-v">{r.value}</span></div>
          <div className="meter" style={{ margin: 0 }}><span style={{ width: r.pct + '%', background: r.color || 'var(--grad-warm)' }}></span></div>
        </div>
      ))}
    </div>
  );
}

function Donut({ segments, label, sub, size = 150 }) {
  let acc = 0; const stops = [];
  segments.forEach(s => { stops.push(`${s.color} ${acc}% ${acc + s.pct}%`); acc += s.pct; });
  return (
    <div className="donut-wrap">
      <div className="donut" style={{ width: size, height: size, background: `conic-gradient(${stops.join(',')})` }}>
        <div className="donut-hole"><div><div className="dh-v">{label}</div><div className="dh-k">{sub}</div></div></div>
      </div>
      <div className="donut-legend">
        {segments.map((s, i) => (
          <div key={i} className="dl"><span className="sw" style={{ background: s.color }}></span>{s.label}<span className="dl-v">{s.value}</span></div>
        ))}
      </div>
    </div>
  );
}

function MetricShell({ go, eyebrow, title, desc, value, unit, trendNote, trendData, trendColor, children }) {
  return (
    <div className="page fade-in">
      <div className="page-head" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="row gap-10" style={{ marginBottom: 8 }}>
            <button className="btn btn-quiet" style={{ padding: 6 }} onClick={() => go('home')} title="Back to dashboard"><I.chevron size={16} style={{ transform: 'rotate(180deg)' }} /></button>
            <span className="chip">{eyebrow}</span>
          </div>
          <h1 className="page-title">{title}</h1>
          <p className="page-desc" style={{ maxWidth: 520 }}>{desc}</p>
        </div>
        <div className="metric-hero">
          <div className="mh-v">{value}{unit && <small> {unit}</small>}</div>
          <div className="mh-sub up">{trendNote}</div>
          <div style={{ marginTop: 12 }}><Trend data={trendData} color={trendColor || 'var(--brand-orange)'} height={56} /></div>
        </div>
      </div>
      {children}
    </div>
  );
}

const Section = ({ title, right, children, style, ...rest }) => (
  <div className="card card-pad" style={style} {...rest}>
    <div className="sec-head"><span className="sec-title">{title}</span>{right}</div>
    {children}
  </div>
);

function RecentTable({ openJob, rows }) {
  return (
    <div className="tbl-scroll">
    <table className="tbl">
      <thead><tr><th>Build</th><th>Client</th><th>Status</th><th>{rows.col || 'Tokens / $'}</th></tr></thead>
      <tbody>
        {rows.data.map(j => {
          const c = window.MAV.clientBySlug(j.client);
          return (
            <tr key={j.id} className="clickable" onClick={() => openJob(j.id)}>
              <td><div className="nm">{j.name.length > 24 ? j.name.slice(0, 24) + '…' : j.name}</div><div className="mono" style={{ fontSize: 11 }}>{j.id}</div></td>
              <td><div className="row gap-8"><ClientBadge slug={j.client} size={22} /><span style={{ fontSize: 12.5 }}>{c.name}</span></div></td>
              <td><StatusPill status={j.status} verdict={j.verdict} /></td>
              <td className="mono">{rows.cell(j)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

/* ============================================================
   1 · Builds this month  (Sorties)
   ============================================================ */
function MetricBuilds({ go, openJob }) {
  const lv = liveMetricsView();
  const summary = window.MAV.summary;
  const totalBuilds = summary && summary.total_builds != null ? Number(summary.total_builds) : 128;
  // Daily bars: prefer live daily counts; mock fallback if metrics absent
  const live = window.MAV.metrics;
  const daily = (live && live.dailyBuilds && live.dailyBuilds.length)
    ? live.dailyBuilds.map((d) => Number(d.count))
    : [3,5,4,6,5,7,4, 6,5,8,6,7,5,9, 6,4,7,8,6,9,7, 5,8,6,7,9,6,8, 7,9];
  const weekly = [78, 92, 101, 115, totalBuilds];

  // Verdict donut from live counts
  const verdictDonut = lv ? [
    { label: 'PASS',        pct: lv.verdict.pctOf('PASS'),        value: String(lv.verdict.pass),        color: 'var(--pass)' },
    { label: 'CONDITIONAL', pct: lv.verdict.pctOf('CONDITIONAL'), value: String(lv.verdict.conditional), color: 'var(--cond)' },
    { label: 'FAIL',        pct: lv.verdict.pctOf('FAIL'),        value: String(lv.verdict.fail),        color: 'var(--fail)' },
  ] : [
    { label: 'PASS', pct: 81, value: '104', color: 'var(--pass)' },
    { label: 'CONDITIONAL', pct: 11, value: '14', color: 'var(--cond)' },
    { label: 'FAIL', pct: 8, value: '10', color: 'var(--fail)' },
  ];
  const sourceRows = lv && lv.source.rows.length
    ? lv.source.rows.map((r) => ({ label: r.label, pct: r.pct, value: String(r.count), color: r.label === 'PDF' ? 'var(--brand-amber)' : 'var(--running)' }))
    : [
        { label: 'PDF', pct: 72, value: '92', color: 'var(--brand-amber)' },
        { label: 'Figma', pct: 28, value: '36', color: 'var(--running)' },
      ];
  const clientRows = lv ? lv.clients.buildsRows.map((r) => ({ ...r, color: 'var(--grad-warm)' }))
    : [
        { label: 'Synchrony', pct: 100, value: '41', color: 'var(--grad-warm)' },
        { label: 'The General', pct: 54, value: '22', color: 'var(--grad-warm)' },
        { label: 'Cricket Wireless', pct: 44, value: '18', color: 'var(--grad-warm)' },
        { label: 'Snowflake', pct: 39, value: '16', color: 'var(--grad-warm)' },
        { label: 'Shippo', pct: 29, value: '12', color: 'var(--grad-warm)' },
        { label: '17 others', pct: 46, value: '19', color: 'var(--queued)' },
      ];

  return (
    <div data-metric="builds" data-metric-live={lv ? '1' : '0'}>
    <MetricShell go={go} eyebrow={lv ? `Sorties · workspace lifetime` : 'Sorties · May 2026'} title="Builds this month"
      desc={lv ? `${totalBuilds} builds across this workspace. PDF and Figma sources mixed; verdict breakdown below.` : '128 builds flown across the squadron this month — up 18% on April, your busiest run yet.'}
      value={String(totalBuilds)} trendNote={lv ? `workspace lifetime · ${lv.source.total} builds` : '▲ 18% vs April · 4.3 / day'} trendData={weekly}>
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">Delivered (PASS)</div><div className="kpi-v" data-kpi="passed">{lv ? lv.verdict.pass : 104}</div></div>
        <div className="kpi"><div className="kpi-k">Conditional</div><div className="kpi-v" data-kpi="conditional">{lv ? lv.verdict.conditional : 14}</div></div>
        <div className="kpi"><div className="kpi-k">Failed</div><div className="kpi-v" data-kpi="failed">{lv ? lv.verdict.fail : 10}</div></div>
        <div className="kpi"><div className="kpi-k">{lv ? 'Awaiting verdict' : 'Busiest day'}</div><div className="kpi-v" data-kpi="pending">{lv ? lv.verdict.nullCount : <>9 <small>May 21</small></>}</div></div>
      </div>
      <div className="metric-grid">
        <Section title="Builds per day" right={<span className="muted" style={{ fontSize: 12 }}>{lv ? 'last 30 days' : 'May 1 – 30'}</span>}>
          <Bars data={daily} fill xlabels={lv ? null : ['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4', '']} />
        </Section>
        <Section title="By verdict">
          <Donut label={`${lv ? lv.verdict.pctOf('PASS') : 81}%`} sub="PASS" segments={verdictDonut} />
        </Section>
      </div>
      <div className="metric-grid mt-20">
        <Section title="Top clients">
          <Breakdown rows={clientRows} />
        </Section>
        <Section title="By source">
          <Breakdown rows={sourceRows} />
        </Section>
      </div>
      <Section title="Recent builds" style={{ marginTop: 20 }} right={<button className="sec-link" onClick={() => go('builds')}>All builds <I.chevron size={14} /></button>}>
        <RecentTable openJob={openJob} rows={{ data: window.MAV.jobs.slice(0, 5), cell: (j) => `${j.tokensK}K · $${Number(j.cost || 0).toFixed(2)}` }} />
      </Section>
    </MetricShell>
    </div>
  );
}

/* ============================================================
   2 · Pass rate
   ============================================================ */
function MetricPass({ go, openJob }) {
  const lv = liveMetricsView();
  const summary = window.MAV.summary;
  const verdicted = summary && summary.verdicted != null ? Number(summary.verdicted) : 38;
  const passed = summary && summary.passed != null ? Number(summary.passed) : 30;
  const passRate = verdicted > 0 ? Math.round((passed / verdicted) * 100) : 91;
  const weekly = [86, 89, 88, 90, passRate];
  // "Where builds land" shows the verdict distribution, so it divides by VERDICTED
  // builds (PASS+CONDITIONAL+FAIL) — NOT by every build. The shared pctOf helper
  // divides by verdictTotal, which includes the still-awaiting (null) group; that
  // made the donut read 8% PASS while the hero passRate read 10%, and left a
  // phantom empty arc for the un-verdicted builds. Awaiting keeps its own KPI tile;
  // here the three segments now sum to 100% and the center matches the hero.
  const _vTot = lv ? (lv.verdict.pass + lv.verdict.conditional + lv.verdict.fail) : 0;
  const _pctV = (n) => (_vTot > 0 ? Math.round((n / _vTot) * 100) : 0);
  const passPct = lv ? _pctV(lv.verdict.pass) : 81;
  const condPct = lv ? _pctV(lv.verdict.conditional) : 10;
  const failPct = lv ? _pctV(lv.verdict.fail) : 9;
  return (
    <div data-metric="pass" data-metric-live={lv ? '1' : '0'}>
    <MetricShell go={go} eyebrow="First-pass quality" title="Pass rate"
      desc={lv ? `${passRate}% of verdicted builds passed clean. ${verdicted} verdicts across ${lv.verdict.total} builds.` : '91% of builds shipped clean or after a quick review this month. Your wingman caught the rest before they flew.'}
      value={String(passRate)} unit="%" trendNote={lv ? `${passed} of ${verdicted} verdicted` : '▲ 3 pts vs April'} trendData={weekly} trendColor="var(--pass)">
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">PASS verdicts</div><div className="kpi-v" data-kpi="pass">{lv ? lv.verdict.pass : 81}<small>{lv ? '' : '%'}</small></div></div>
        <div className="kpi"><div className="kpi-k">CONDITIONAL</div><div className="kpi-v" data-kpi="conditional">{lv ? lv.verdict.conditional : 91}<small>{lv ? '' : '%'}</small></div></div>
        <div className="kpi"><div className="kpi-k">FAIL</div><div className="kpi-v" data-kpi="fail">{lv ? lv.verdict.fail : 9}<small>{lv ? '' : '%'}</small></div></div>
        <div className="kpi"><div className="kpi-k">{lv ? 'Awaiting verdict' : 'Avg review time'}</div><div className="kpi-v" data-kpi="pending">{lv ? lv.verdict.nullCount : <>6<small>min</small></>}</div></div>
      </div>
      <div className="metric-grid">
        <Section title="Why builds need a second look" right={<span className="muted" style={{ fontSize: 12 }}>24 flagged</span>}>
          <Breakdown rows={[
            { label: 'Broken image reference (hard gate)', pct: 34, value: '34%', color: 'var(--fail)' },
            { label: 'Rate-table legibility', pct: 19, value: '19%', color: 'var(--cond)' },
            { label: 'Disclaimer / color mismatch', pct: 14, value: '14%', color: 'var(--cond)' },
            { label: 'Missing alt text', pct: 12, value: '12%', color: 'var(--cond)' },
            { label: 'CTA href / UTM', pct: 9, value: '9%', color: 'var(--cond)' },
            { label: 'Other', pct: 12, value: '12%', color: 'var(--queued)' },
          ]} />
        </Section>
        <Section title="Where builds land">
          <Donut label={`${passPct}%`} sub="PASS" segments={[
            { label: 'PASS', pct: passPct, value: lv ? String(lv.verdict.pass) : '81%', color: 'var(--pass)' },
            { label: 'CONDITIONAL', pct: condPct, value: lv ? String(lv.verdict.conditional) : '10%', color: 'var(--brand-amber)' },
            { label: 'FAIL', pct: failPct, value: lv ? String(lv.verdict.fail) : '9%', color: 'var(--fail)' },
          ]} />
        </Section>
      </div>
      <div className="metric-grid mt-20">
        <Section title="Pass rate by client" right={lv ? <span className="muted" style={{ fontSize: 11 }}>≥1 build, sorted by pass %</span> : null}>
          <Breakdown rows={lv && lv.clients.passRows.length ? lv.clients.passRows : [
            { label: 'Shippo', pct: 98, value: '98%', color: 'var(--pass)' },
            { label: 'The General', pct: 95, value: '95%', color: 'var(--pass)' },
            { label: 'Cricket Wireless', pct: 93, value: '93%', color: 'var(--pass)' },
            { label: 'Synchrony', pct: 89, value: '89%', color: 'var(--pass)' },
            { label: 'Snowflake', pct: 86, value: '86%', color: 'var(--cond)' },
            { label: 'Vancity', pct: 79, value: '79%', color: 'var(--cond)' },
          ]} />
        </Section>
        <Section title="Recently flagged" right={<button className="sec-link" onClick={() => go('queue')}>Review queue <I.chevron size={14} /></button>}>
          <RecentTable openJob={openJob} rows={{ col: 'Confidence', data: window.MAV.jobs.filter(j => j.status === 'needs_review' || j.verdict === 'FAIL').slice(0, 6), cell: (j) => j.confidence != null ? `${Math.round(j.confidence * 100)}%` : '—' }} />
        </Section>
      </div>
    </MetricShell>
    </div>
  );
}

/* ============================================================
   3 · Avg build time
   ============================================================ */
function MetricTime({ go, openJob }) {
  const lv = liveMetricsView();
  // Per-build elapsed aggregates (server /api/data/metrics): fastest/slowest/
  // median build, avg time by source, weekly trend. Each falls back to the
  // prototype mock when the field is absent (older server build / no data).
  const m = window.MAV.metrics || {};
  const bt = (m.buildTime && Number(m.buildTime.builds) > 0) ? m.buildTime : null;
  const toMin = (s) => (Number(s) / 60).toFixed(1);   // seconds → "X.X" minutes
  const weekly = (m.elapsedByWeek && m.elapsedByWeek.length)
    ? m.elapsedByWeek.map((r) => Number(r.avg_s) / 60)   // live min/week
    : [3.1, 3.0, 2.9, 2.8, 2.7];
  const stages = lv && lv.avgStageRows.length
    ? lv.avgStageRows.map((s) => [s.label, s.seconds])
    : [['Ingestion', 11], ['Semantic', 41], ['Planning', 21], ['Execution', 53], ['QA', 30], ['Approval', 9]];
  const totalS = stages.reduce((a, s) => a + s[1], 0);
  const avgMin = totalS > 0 ? (totalS / 60) : 2.7;
  // Headline = median of per-build TOTAL elapsed (the same stat the dashboard
  // tile shows + the "Median build" KPI below), so the tile and this screen
  // agree on one "typical build" number. `avgMin` (sum of per-stage averages)
  // is a synthetic full-pipeline figure — a build running all six stages at
  // their mean speed, which no single-stage codex build ever does — so it's
  // demoted to the "Total stage avg" KPI, never the headline. Falls back to
  // avgMin only when per-build timing is absent (older server / no builds).
  const headlineMin = bt ? toMin(bt.median_s) : (lv ? avgMin.toFixed(1) : '2.7');
  const maxS = Math.max(...stages.map(s => s[1])) || 1;
  const slowest = [...window.MAV.jobs].sort((a, b) => b.tokensK - a.tokensK).slice(0, 4);
  return (
    <div data-metric="time" data-metric-live={lv ? '1' : '0'}>
    <MetricShell go={go} eyebrow="Throughput" title="Avg build time"
      desc={bt ? `${headlineMin} minutes — the median build's total time (the typical build). Execution and the Sonnet semantic parse own most of the clock.` : (lv ? `${avgMin.toFixed(1)} minutes summed across the six stage averages. Execution and the Sonnet semantic parse own most of the clock.` : '2.7 minutes median across the six stages. Execution and the Sonnet semantic parse own most of the clock — the need for speed.')}
      value={headlineMin} unit="min" trendNote={bt ? `live · median per-build total` : (lv ? `live · sum of per-stage avg elapsed` : '▼ 13% faster vs April')} trendData={weekly} trendColor="var(--pass)">
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">{lv ? 'Total stage avg' : 'Median'}</div><div className="kpi-v" data-kpi="median">{lv ? avgMin.toFixed(1) : '2.7'}<small>min</small></div></div>
        <div className="kpi"><div className="kpi-k">Fastest</div><div className="kpi-v" data-kpi="fastest">{bt ? toMin(bt.fastest_s) : '1.9'}<small>min</small></div></div>
        <div className="kpi"><div className="kpi-k">Slowest</div><div className="kpi-v" data-kpi="slowest">{bt ? toMin(bt.slowest_s) : '4.8'}<small>min</small></div></div>
        <div className="kpi"><div className="kpi-k">{bt ? 'Median build' : 'Avg queue wait'}</div><div className="kpi-v" data-kpi="median-build">{bt ? <React.Fragment>{toMin(bt.median_s)}<small>min</small></React.Fragment> : <React.Fragment>8<small>sec</small></React.Fragment>}</div></div>
      </div>
      <div className="metric-grid">
        <Section title="Time per stage" right={<span className="muted mono" style={{ fontSize: 12 }}>{lv ? `~${Math.round(totalS)}s total` : '~165s total'}</span>}>
          <Breakdown rows={stages.map(([l, s]) => ({
            label: l, pct: Math.round(s / maxS * 100), value: s + 's',
            color: l === 'Execution' ? 'var(--brand-orange)' : l === 'Semantic' ? 'var(--brand-amber)' : 'var(--grad-warm)',
          }))} />
        </Section>
        <div className="col gap-20">
          <Section title="By source" right={(m.elapsedBySource && m.elapsedBySource.length) ? <span className="muted" style={{ fontSize: 11 }}>avg · live</span> : null}>
            <Breakdown rows={(() => {
              const rows = m.elapsedBySource || [];
              if (rows.length) {
                const max = rows.reduce((a, r) => Math.max(a, Number(r.avg_s)), 0.0001);
                return rows.map((r) => ({
                  label: r.source === 'pdf' ? 'PDF' : (r.source ? r.source.charAt(0).toUpperCase() + r.source.slice(1) : '—'),
                  pct: Math.round(Number(r.avg_s) / max * 100), value: toMin(r.avg_s) + ' min',
                  color: r.source === 'figma' ? 'var(--running)' : 'var(--brand-amber)',
                }));
              }
              return [
                { label: 'PDF', pct: 78, value: '2.5 min', color: 'var(--brand-amber)' },
                { label: 'Figma', pct: 100, value: '3.2 min', color: 'var(--running)' },
              ];
            })()} />
          </Section>
          <Section title="Trend" right={(m.elapsedByWeek && m.elapsedByWeek.length) ? <span className="muted" style={{ fontSize: 11 }}>min/wk · live</span> : null}>
            <Bars data={weekly} height={90} xlabels={weekly.map((_, i) => i === Math.floor(weekly.length / 2) ? 'wks' : '')} color="var(--grad-warm)" />
          </Section>
        </div>
      </div>
      <div className="metric-grid mt-20">
        <Section title="Slowest builds this week">
          <RecentTable openJob={openJob} rows={{ col: 'Tokens', data: slowest, cell: (j) => `${j.tokensK}K` }} />
        </Section>
        <Section title="Stage notes">
          <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, margin: 0 }}>
            Execution (Gemini vision) and the Sonnet semantic parse are the two heaviest legs. Figma builds add ~0.7 min fetching frame assets over the REST API. Cached stages on a resume skip straight to the failed leg — most retries land in under a minute.
          </p>
        </Section>
      </div>
    </MetricShell>
    </div>
  );
}

/* ============================================================
   4 · Token spend  (Fuel)
   ============================================================ */
function MetricSpend({ go, openJob }) {
  // VD-01/PY-22: under a codex-default workspace the $ fuel gauge is nonsense ($0 sub) → the
  // CodexUsagePanel replaces it. Same RBAC.activeEngine signal the dashboard gates on (VD-03).
  if (window.RBAC && window.RBAC.activeEngine && window.RBAC.activeEngine() === 'codex' && window.CodexUsagePanel) {
    return <div data-metric="spend" data-spend-source="codex"><CodexUsagePanel /></div>;
  }
  const lv = liveMetricsView();
  const summary = window.MAV.summary;
  const cbs = window.MAV.costByStage;
  const totalCost = summary && summary.total_cost != null ? Number(summary.total_cost) : (cbs ? cbs.totalCost : 340);
  const builds = summary && summary.total_builds != null ? Number(summary.total_builds) : 0;

  // LIVE OpenRouter account balance — the fuel gauge's source of truth (real
  // spend / cap / remaining), fetched on mount and cached on MAV.credits across
  // screens. Falls back to our DB sum vs a static cap when unavailable (no
  // management key / OpenRouter down) so the screen still renders.
  const [credits, setCredits] = React.useState(window.MAV.credits || null);
  React.useEffect(() => {
    let alive = true;
    if (window.GooseAPI && window.GooseAPI.openrouterCredits) {
      window.GooseAPI.openrouterCredits()
        .then((d) => { if (alive && d) { window.MAV.credits = d; setCredits(d); } })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, []);
  const orLive = !!(credits && credits.available && credits.remaining != null);

  // Top-row fuel gauge = OpenRouter when live (internally consistent: spent +
  // remaining = cap, avg = spent/builds). Breakdowns below stay DB-tracked.
  const STATIC_CAP = 500;
  const anyLive = orLive || !!lv || !!cbs;
  const realSpent = orLive ? Number(credits.total_usage) : totalCost;
  const realCap = orLive ? Number(credits.total_credits) : STATIC_CAP;
  const realRemaining = orLive ? Number(credits.remaining) : Math.max(0, STATIC_CAP - totalCost);
  const pctUsed = realCap > 0 ? Math.round((realSpent / realCap) * 100) : 0;
  const avgPerBuild = builds > 0 ? realSpent / builds : 0.18;
  // Reconciliation: our DB attributes `tracked` of the real spend per stage/
  // model; the rest is untracked (QA/vision stages don't record cost — ISS-07).
  const tracked = totalCost;
  const untracked = orLive ? Math.max(0, realSpent - tracked) : 0;
  const weekly = [62, 78, 95, Math.round(realSpent)];
  return (
    <div data-metric="spend" data-metric-live={anyLive ? '1' : '0'} data-spend-source={orLive ? 'openrouter' : 'db'}>
    <MetricShell go={go} eyebrow={orLive ? 'Fuel · live · OpenRouter' : (anyLive ? 'Fuel · lifetime' : 'Fuel · May')} title="Token spend"
      desc={orLive
        ? `$${realSpent.toFixed(2)} of your $${realCap.toFixed(0)} OpenRouter balance — ${pctUsed}% burned, $${realRemaining.toFixed(2)} left. Goose attributes $${tracked.toFixed(2)} of it to build stages; $${untracked.toFixed(2)} is untracked (QA/vision cost).`
        : (anyLive ? `$${totalCost.toFixed(2)} workspace lifetime · ${builds} builds. Cap $${STATIC_CAP} (static — OpenRouter balance unavailable).` : '$340 of your $500 monthly budget — 68% of the tank.')}
      value={`$${anyLive ? realSpent.toFixed(2) : '340'}`}
      trendNote={anyLive ? `${pctUsed}% of $${realCap.toFixed(0)} ${orLive ? 'balance' : 'cap'}` : '68% of $500 cap'} trendData={weekly}>
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">Spent{orLive ? ' (actual)' : ''}</div><div className="kpi-v" data-kpi="spent">${anyLive ? realSpent.toFixed(2) : '340'}</div></div>
        <div className="kpi"><div className="kpi-k">Remaining</div><div className="kpi-v" style={{ color: realRemaining > 0 ? 'var(--pass)' : 'var(--fail)' }} data-kpi="remaining">${anyLive ? realRemaining.toFixed(2) : '160'}</div></div>
        <div className="kpi"><div className="kpi-k">Avg / build</div><div className="kpi-v" data-kpi="avg">${anyLive ? avgPerBuild.toFixed(3) : '0.18'}</div></div>
        <div className="kpi"><div className="kpi-k">{orLive ? 'Balance' : 'Budget'}</div><div className="kpi-v" data-kpi="cap">${anyLive ? realCap.toFixed(0) : '500'}</div></div>
      </div>
      <Section title="Budget burn-down" right={<span className={'chip ' + (pctUsed >= 90 ? 'pill-fail' : 'pill-cond')} style={{ fontSize: 10.5 }}>Soft warn at 90%</span>} style={{ marginBottom: 20 }}>
        <div className="row between" style={{ fontSize: 12.5, marginBottom: 8 }}>
          <span className="muted">${anyLive ? realSpent.toFixed(2) : '340'} spent · ${anyLive ? realRemaining.toFixed(2) : '160'} left</span>
          <span className="mono muted">{orLive ? 'live · OpenRouter balance' : 'lifetime — month filter pending'}</span>
        </div>
        <div className={'meter' + (pctUsed >= 90 ? ' warn' : '')} style={{ height: 12, margin: 0 }}><span style={{ width: Math.min(100, pctUsed) + '%' }}></span></div>
        {orLive
          ? <p className="muted" style={{ fontSize: 11.5, marginTop: 9 }}>Goose attributes <b>${tracked.toFixed(2)}</b> to builds still on disk; the <b>${untracked.toFixed(2)}</b> difference is mostly off-disk lifetime spend — builds whose run dirs were cleaned (not a tracking gap — ISS-10).{pctUsed >= 90 ? <span style={{ color: 'var(--fail)' }}> Balance under 10% — top up to avoid build failures.</span> : ''}</p>
          : <p className="muted" style={{ fontSize: 11.5, marginTop: 9 }}>One Max-quality (Opus orchestrator) run costs ~17× a DeepSeek-tier run — the spread admins gate per role.</p>}
      </Section>
      <div className="metric-grid">
        <Section title="Cost by stage" right={orLive ? <span className="muted" style={{ fontSize: 11 }}>Goose-tracked · ${tracked.toFixed(2)}</span> : null}>
          <Breakdown rows={(() => {
            if (cbs && cbs.rows && cbs.rows.length) {
              const positive = cbs.rows.filter((r) => Number(r.cost) > 0);
              const total = positive.reduce((a, r) => a + Number(r.cost), 0) || 1;
              const PALETTE = ['var(--brand-orange)', 'var(--brand-amber)', 'var(--brand-yellow)', 'var(--queued)', 'var(--running)', 'var(--cond)'];
              return [...positive].sort((a, b) => Number(b.cost) - Number(a.cost)).map((r, i) => ({
                label: r.name, pct: Math.round((Number(r.cost) / total) * 100),
                value: '$' + Number(r.cost).toFixed(2), color: PALETTE[i % PALETTE.length],
              }));
            }
            return [
              { label: 'Semantic (Sonnet)', pct: 42, value: '$143', color: 'var(--brand-orange)' },
              { label: 'Execution (Gemini)', pct: 28, value: '$95', color: 'var(--brand-amber)' },
              { label: 'QA (Perceptron + Gemini)', pct: 18, value: '$61', color: 'var(--brand-yellow)' },
              { label: 'Planning · Approval (DeepSeek)', pct: 12, value: '$41', color: 'var(--queued)' },
            ];
          })()} />
        </Section>
        <Section title="Cost by model" right={lv && lv.costByModelRows.length ? <span className="muted" style={{ fontSize: 11 }}>{lv.costByModelRows.length} models · live</span> : null} data-cost-by-model={lv && lv.costByModelRows.length ? 'live' : 'mock'}>
          <Breakdown rows={lv && lv.costByModelRows.length ? lv.costByModelRows : [
            { label: 'claude-sonnet-4.6', pct: 100, value: '$143', color: 'var(--grad-warm)' },
            { label: 'gemini-3.5-flash', pct: 67, value: '$96', color: 'var(--grad-warm)' },
            { label: 'perceptron-mk1', pct: 43, value: '$61', color: 'var(--grad-warm)' },
            { label: 'deepseek-v4-pro', pct: 28, value: '$40', color: 'var(--queued)' },
          ]} />
        </Section>
      </div>
      <div className="metric-grid mt-20">
        <Section title="Cost by client">
          <Breakdown rows={lv && lv.clients.costRows.length ? lv.clients.costRows.map((r) => ({ ...r, color: 'var(--grad-warm)' })) : [
            { label: 'Synchrony', pct: 100, value: '$118', color: 'var(--grad-warm)' },
            { label: 'The General', pct: 51, value: '$61', color: 'var(--grad-warm)' },
            { label: 'Snowflake', pct: 40, value: '$47', color: 'var(--grad-warm)' },
            { label: 'Cricket Wireless', pct: 33, value: '$39', color: 'var(--grad-warm)' },
            { label: 'Others', pct: 64, value: '$75', color: 'var(--queued)' },
          ]} />
        </Section>
        <Section title="Priciest builds" right={<button className="sec-link" onClick={() => go('builds')}>All builds <I.chevron size={14} /></button>}>
          <RecentTable openJob={openJob} rows={{ data: [...window.MAV.jobs].sort((a, b) => Number(b.cost || 0) - Number(a.cost || 0)).slice(0, 5), cell: (j) => `$${Number(j.cost || 0).toFixed(2)}` }} />
        </Section>
      </div>
    </MetricShell>
    </div>
  );
}

Object.assign(window, { MetricBuilds, MetricPass, MetricTime, MetricSpend,
  Trend, Bars, Breakdown, Donut, Section, MetricShell });
